import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { AlertCircle, X, RefreshCw } from 'lucide-react';

const KEY = 'lumera_pending_upgrade';
const MAX_AGE_MS = 60 * 60 * 1000; // 1 hour
const HIDE_ON = ['/payment/success', '/login', '/mock-payment'];

// App-wide banner that offers a one-tap retry when a checkout was started but
// never completed (abandoned tab or a declined card that bounced the user back).
export const PaymentRetryBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const { language } = useLanguage();
  const [pending, setPending] = useState(null);

  const read = () => {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data?.planId || !data?.ts || Date.now() - data.ts > MAX_AGE_MS) {
        localStorage.removeItem(KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    // Already premium? nothing to recover.
    if (user && (user.subscription_tier === 'premium' || user.role === 'super_admin' || user.role === 'moderator')) {
      try { localStorage.removeItem(KEY); } catch (e) {}
      setPending(null);
      return;
    }
    setPending(read());
  }, [location.pathname, user]);

  if (loading) return null;
  if (!pending) return null;
  if (HIDE_ON.some((p) => location.pathname.startsWith(p))) return null;

  const dismiss = () => {
    try { localStorage.removeItem(KEY); } catch (e) {}
    setPending(null);
  };

  const retry = () => {
    navigate(`/pricing?resume=${encodeURIComponent(pending.planId)}&provider=${encodeURIComponent(pending.provider || 'stripe')}`);
  };

  const goYearly = () => {
    navigate(`/pricing?resume=premium_yearly&provider=${encodeURIComponent(pending.provider || 'stripe')}`);
  };

  const showUpsell = pending.planId === 'premium';

  return (
    <div
      data-testid="payment-retry-banner"
      className="fixed top-0 inset-x-0 z-[120] bg-amber-500 text-amber-950 shadow-md"
    >
      <div className="max-w-5xl mx-auto flex items-center gap-3 px-4 py-2.5 text-sm">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1 font-medium">
          {language === 'es'
            ? 'Parece que tu mejora no se completó. ¿Deseas intentarlo de nuevo?'
            : "Looks like your upgrade didn't finish. Want to try again?"}
        </span>
        <button
          onClick={retry}
          data-testid="payment-retry-btn"
          className="inline-flex items-center gap-1.5 bg-amber-950 text-amber-50 rounded-full px-3.5 py-1.5 text-xs font-semibold hover:bg-amber-900 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {language === 'es' ? 'Reintentar' : 'Try again'}
        </button>
        {showUpsell && (
          <button
            onClick={goYearly}
            data-testid="payment-retry-yearly-btn"
            className="hidden sm:inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold bg-green-700 text-white hover:bg-green-800 transition-colors"
          >
            {language === 'es' ? 'Anual · ahorra 17%' : 'Go yearly · save 17%'}
          </button>
        )}
        <button
          onClick={dismiss}
          data-testid="payment-retry-dismiss"
          aria-label="Dismiss"
          className="p-1 rounded hover:bg-amber-600/40 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default PaymentRetryBanner;
